import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
    Store, ExternalLink, Copy, Plus, Trash2, ImagePlus, Eye, ShoppingBag, Heart, DollarSign, MessageCircle, Users, Folders,
    Palette, Home, FileText, Search, LineChart, Share2, Globe, Save, Settings2, Bell, ArrowUpRight, Sparkles, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useShareConfig } from '@/context/ShareConfigContext';
import { Link } from 'react-router-dom';
import { DomainWizard } from '@/components/dashboard/DomainWizard';

async function uploadImage(file) {
    const fd = new FormData(); fd.append('file', file); fd.append('kind', 'image');
    const { data } = await api.post('/upload', fd);
    return data.url;
}

const THEMES = [
    { code: 'minimal', name: 'Minimal', preview: 'bg-white text-neutral-900 border', accent: 'from-slate-100 to-white', desc: 'Limpio, elegante, tipografía serif.' },
    { code: 'bold', name: 'Bold', preview: 'bg-gradient-to-br from-red-600 to-black text-white', accent: 'from-red-100 to-red-50', desc: 'Impacto visual, gradientes rojos.' },
    { code: 'editorial', name: 'Editorial', preview: 'bg-neutral-950 text-white', accent: 'from-slate-900 to-slate-700', desc: 'Estilo revista, negro sobrio.' },
];

// Industry templates (Phase 20g) — 1-click preset packs
const INDUSTRY_TEMPLATES = [
    {
        code: 'electronica',
        name: 'Electrónica',
        tagline: 'Tech premium para tu día a día',
        emoji: '📱',
        preview: 'from-slate-900 via-blue-900 to-slate-950',
        preset: {
            theme: 'editorial',
            primary_color: '#0ea5e9',
            accent_color: '#0f172a',
            hero_headline: 'La mejor tecnología, al mejor precio',
            hero_cta_label: 'Explorar catálogo',
            announcement_bar_enabled: true,
            announcement_bar_text: '⚡ Envío express en pedidos +$999',
            faq: [
                { q: '¿Los productos son originales?', a: 'Sí, todos vienen sellados y con garantía del fabricante.' },
                { q: '¿Cuál es el tiempo de entrega?', a: '24-72h en zona metropolitana; 3-5 días al resto del país.' },
            ],
            policies_shipping: 'Envíos con DHL/FedEx asegurados. Empaque anti-golpes y rastreo en tiempo real.',
            policies_returns: 'Devoluciones gratis hasta 14 días después de la entrega si el producto tiene defecto.',
        },
    },
    {
        code: 'moda',
        name: 'Moda',
        tagline: 'Tu estilo, tu firma',
        emoji: '👗',
        preview: 'from-rose-500 via-pink-500 to-fuchsia-600',
        preset: {
            theme: 'bold',
            primary_color: '#e11d48',
            accent_color: '#f43f5e',
            hero_headline: 'Nueva colección: expresa quien eres',
            hero_cta_label: 'Ver lookbook',
            announcement_bar_enabled: true,
            announcement_bar_text: '🎁 Envío gratis con tu primera compra',
            faq: [
                { q: '¿Puedo cambiar la talla?', a: 'Sí, tienes 15 días para cambios de talla sin costo.' },
                { q: '¿Hacen envíos a todo el país?', a: 'Sí, con DHL/Estafeta a toda la República.' },
            ],
            policies_shipping: 'Envío gratis en compras +$999. Empaquetado especial con papel de seda.',
            policies_returns: 'Cambio o reembolso completo hasta 30 días después de la entrega.',
        },
    },
    {
        code: 'hogar',
        name: 'Hogar',
        tagline: 'Tu casa, tu refugio',
        emoji: '🏡',
        preview: 'from-amber-400 via-orange-500 to-red-500',
        preset: {
            theme: 'minimal',
            primary_color: '#f59e0b',
            accent_color: '#78350f',
            hero_headline: 'Renueva tu espacio, transforma tu día',
            hero_cta_label: 'Ver productos',
            announcement_bar_enabled: true,
            announcement_bar_text: '🛋️ Meses sin intereses en muebles',
            faq: [
                { q: '¿Ofrecen ensamble?', a: 'Sí, en CDMX/GDL/MTY con costo adicional según el producto.' },
                { q: '¿Cuáles son los tiempos de entrega?', a: '5-7 días hábiles para muebles grandes; 2-3 para decoración.' },
            ],
            policies_shipping: 'Muebles con entrega hasta la puerta. Empaque reforzado y rastreo.',
            policies_returns: 'Cambio en 7 días si el producto llega dañado. Reembolso vía OkWallet o método original.',
        },
    },
];

// ---------- KPI card ----------
const KPI = ({ icon: Icon, label, value, delta, testId }) => (
    <Card data-testid={testId}>
        <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />{label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{value ?? '—'}</div>
            {delta != null && (
                <div className={`mt-1 text-[11px] font-medium ${delta >= 0 ? 'text-emerald-700' : 'text-destructive'}`}>{delta >= 0 ? '+' : ''}{delta}%</div>
            )}
        </CardContent>
    </Card>
);

// ---------- Setup Checklist ----------
const SetupChecklist = ({ cfg }) => {
    if (!cfg) return null;
    const checks = [
        { ok: !!cfg.logo_url, label: 'Sube tu logo' },
        { ok: !!cfg.banner_url, label: 'Sube tu banner' },
        { ok: !!cfg.display_name, label: 'Nombre de tienda' },
        { ok: !!cfg.tagline, label: 'Tagline / lema' },
        { ok: !!cfg.about, label: 'Cuenta tu historia (Acerca)' },
        { ok: !!(cfg.policies_shipping || cfg.policies_returns || cfg.policies_privacy), label: 'Políticas de envío/devoluciones/privacidad' },
        { ok: !!(cfg.socials && Object.values(cfg.socials).some(Boolean)), label: 'Conecta redes sociales' },
        { ok: !!cfg.seo_title || !!cfg.seo_description, label: 'Configura SEO' },
        { ok: !!cfg.enabled, label: 'Publica tu tienda' },
    ];
    const done = checks.filter(c => c.ok).length;
    const pct = Math.round((done / checks.length) * 100);
    return (
        <Card data-testid="setup-checklist">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold">Configuración de tu tienda</h3>
                    </div>
                    <Badge variant="outline">{done}/{checks.length} completado</Badge>
                </div>
                <Progress value={pct} className="h-2 mb-3" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {checks.map((c, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${c.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-600'}`}>
                            <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[10px] ${c.ok ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-white'}`}>{c.ok ? '✓' : '·'}</span>
                            {c.label}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// ---------- Image Field ----------
const ImageField = ({ label, value, onChange, hint, testId }) => {
    const [uploading, setUploading] = useState(false);
    const pick = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setUploading(true);
        try { const url = await uploadImage(f); onChange(url); toast.success('Imagen cargada'); }
        catch { toast.error('Error subiendo imagen'); }
        finally { setUploading(false); }
    };
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="mt-1 flex gap-2 items-center">
                {value ? (
                    <div className="relative">
                        <img src={value} alt={label} className="w-24 h-16 object-cover rounded border" />
                        <button type="button" onClick={() => onChange('')} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white border shadow flex items-center justify-center" data-testid={`${testId}-remove`}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                        </button>
                    </div>
                ) : (
                    <div className="w-24 h-16 rounded border-2 border-dashed flex items-center justify-center text-muted-foreground">
                        <ImagePlus className="h-5 w-5" />
                    </div>
                )}
                <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testId} />
                    <div className="px-3 py-1.5 text-xs font-medium rounded border hover:bg-slate-50">{uploading ? 'Subiendo…' : 'Seleccionar'}</div>
                </label>
                {value && <Input value={value} readOnly className="text-xs h-8 flex-1" />}
            </div>
            {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
};

// ---------- Main ----------
export default function DashboardStorefront() {
    const { user } = useAuth();
    const { buildShareUrl } = useShareConfig();
    const [cfg, setCfg] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [collections, setCollections] = useState([]);
    const [ads, setAds] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [busy, setBusy] = useState(false);
    const [colDialog, setColDialog] = useState(false);
    const [editingCol, setEditingCol] = useState(null);
    const [colForm, setColForm] = useState({ name: '', slug: '', description: '', ad_ids: [], cover_url: '', active: true });
    const [faqTmp, setFaqTmp] = useState({ q: '', a: '' });

    const load = async () => {
        try {
            const [sf, an, cols, myAds, msgs] = await Promise.all([
                api.get('/me/storefront'),
                api.get('/me/storefront/analytics').catch(() => ({ data: null })),
                api.get('/me/storefront/collections').catch(() => ({ data: { items: [] } })),
                api.get('/me/ads'),
                api.get('/me/storefront/contacts').catch(() => ({ data: { items: [] } })),
            ]);
            setCfg(sf.data.storefront || {});
            setAnalytics(an.data);
            setCollections(cols.data.items || []);
            setAds(myAds.data || []);
            setContacts(msgs.data.items || []);
        } catch (e) { toast.error('Error al cargar'); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/me/storefront', cfg);
            toast.success('Configuración guardada');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const applyIndustryTemplate = (tpl) => {
        if (!window.confirm(`Aplicar plantilla "${tpl.name}"? Esto sobrescribe tema, colores, hero, barra de anuncio y políticas actuales. No afecta logo/banner ni tus productos.`)) return;
        setCfg(c => ({ ...c, ...tpl.preset, tagline: c.tagline || tpl.tagline }));
        toast.success(`Plantilla ${tpl.name} aplicada. Guarda para publicar.`);
    };

    const patch = (k, v) => setCfg(c => ({ ...c, [k]: v }));
    const patchNested = (k, sk, v) => setCfg(c => ({ ...c, [k]: { ...(c[k] || {}), [sk]: v } }));

    const publicUrl = user?.nickname ? buildShareUrl(`/store/${user.nickname}`) : '';

    // ---------- Collections handlers ----------
    const openColDialog = (col) => {
        setEditingCol(col);
        setColForm(col ? { ...col } : { name: '', slug: '', description: '', ad_ids: [], cover_url: '', active: true });
        setColDialog(true);
    };
    const saveCollection = async () => {
        try {
            if (editingCol?.id) {
                await api.put(`/me/storefront/collections/${editingCol.id}`, colForm);
                toast.success('Colección actualizada');
            } else {
                await api.post('/me/storefront/collections', colForm);
                toast.success('Colección creada');
            }
            setColDialog(false);
            const { data } = await api.get('/me/storefront/collections');
            setCollections(data.items || []);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };
    const deleteCollection = async (id) => {
        if (!window.confirm('¿Eliminar colección?')) return;
        try {
            await api.delete(`/me/storefront/collections/${id}`);
            setCollections(cs => cs.filter(c => c.id !== id));
        } catch (e) { toast.error('Error'); }
    };

    if (!cfg) return <div className="text-sm text-muted-foreground py-10 text-center">Cargando panel de tu tienda…</div>;

    return (
        <div className="space-y-4" data-testid="dashboard-storefront-panel">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 text-white flex items-center justify-center">
                        <Store className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Mi OkStore</h1>
                        <p className="text-xs text-muted-foreground">Configura tu tienda como en Shopify — sin código.</p>
                    </div>
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                        <Label className="text-xs">Publicada</Label>
                        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch('enabled', v)} data-testid="sf-enabled-switch" />
                    </div>
                    {publicUrl && (
                        <>
                            <Button size="sm" variant="outline" asChild data-testid="sf-preview">
                                <a href={publicUrl} target="_blank" rel="noreferrer"><Eye className="h-4 w-4 mr-1" />Ver tienda</a>
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copiado'); }} data-testid="sf-copy-link">
                                <Copy className="h-4 w-4 mr-1" />Link
                            </Button>
                        </>
                    )}
                    <Button size="sm" onClick={save} disabled={busy} data-testid="sf-save"><Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar'}</Button>
                </div>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI icon={ShoppingBag} label="Anuncios" value={analytics?.ads_count ?? ads.length} testId="kpi-ads" />
                <KPI icon={DollarSign} label="Ventas" value={analytics?.orders_count} testId="kpi-orders" />
                <KPI icon={Users} label="Visitas" value={analytics?.storefront_visits} testId="kpi-visits" />
                <KPI icon={Star} label="Rating" value={analytics?.rating_avg?.toFixed?.(1) ?? '—'} testId="kpi-rating" />
            </div>

            <SetupChecklist cfg={cfg} />

            {/* Tabs */}
            <Tabs defaultValue="general">
                <TabsList className="w-full flex-wrap h-auto">
                    <TabsTrigger value="general" data-testid="sf-tab-general"><Store className="h-3.5 w-3.5 mr-1" />General</TabsTrigger>
                    <TabsTrigger value="branding" data-testid="sf-tab-branding"><Palette className="h-3.5 w-3.5 mr-1" />Branding</TabsTrigger>
                    <TabsTrigger value="homepage" data-testid="sf-tab-homepage"><Home className="h-3.5 w-3.5 mr-1" />Homepage</TabsTrigger>
                    <TabsTrigger value="collections" data-testid="sf-tab-collections"><Folders className="h-3.5 w-3.5 mr-1" />Colecciones</TabsTrigger>
                    <TabsTrigger value="pages" data-testid="sf-tab-pages"><FileText className="h-3.5 w-3.5 mr-1" />Páginas</TabsTrigger>
                    <TabsTrigger value="seo" data-testid="sf-tab-seo"><Search className="h-3.5 w-3.5 mr-1" />SEO</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="sf-tab-analytics"><LineChart className="h-3.5 w-3.5 mr-1" />Analíticas</TabsTrigger>
                    <TabsTrigger value="socials" data-testid="sf-tab-socials"><Share2 className="h-3.5 w-3.5 mr-1" />Redes</TabsTrigger>
                    <TabsTrigger value="advanced" data-testid="sf-tab-advanced"><Settings2 className="h-3.5 w-3.5 mr-1" />Avanzado</TabsTrigger>
                    <TabsTrigger value="messages" data-testid="sf-tab-messages"><MessageCircle className="h-3.5 w-3.5 mr-1" />Mensajes ({contacts.length})</TabsTrigger>
                </TabsList>

                {/* GENERAL */}
                <TabsContent value="general" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div><Label>Nombre público</Label>
                                <Input value={cfg.display_name || ''} onChange={(e) => patch('display_name', e.target.value)} placeholder="Mi Súper Tienda" data-testid="sf-display-name" />
                            </div>
                            <div><Label>Tagline / lema</Label>
                                <Input value={cfg.tagline || ''} onChange={(e) => patch('tagline', e.target.value)} placeholder="Lo mejor en electrónica reacondicionada" data-testid="sf-tagline" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Acerca de tu tienda</Label>
                                <Textarea rows={5} value={cfg.about || ''} onChange={(e) => patch('about', e.target.value)} placeholder="Cuenta tu historia, quién eres, qué vendes…" data-testid="sf-about" />
                            </div>
                            <div><Label>Correo de contacto</Label>
                                <Input type="email" value={cfg.contact_email || ''} onChange={(e) => patch('contact_email', e.target.value)} placeholder="contacto@mitienda.com" />
                            </div>
                            <div><Label>Teléfono</Label>
                                <Input value={cfg.contact_phone || ''} onChange={(e) => patch('contact_phone', e.target.value)} placeholder="+52 55 1234 5678" />
                            </div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* BRANDING */}
                <TabsContent value="branding" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-4">
                        {/* Industry Templates (Phase 20g) */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="h-4 w-4 text-primary" />
                                <div className="text-sm font-semibold">Plantillas por industria</div>
                                <Badge variant="outline" className="text-[10px]">1-click</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">Aplica un pack listo para tu industria. Puedes personalizar después.</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                {INDUSTRY_TEMPLATES.map(tpl => (
                                    <button
                                        key={tpl.code}
                                        type="button"
                                        onClick={() => applyIndustryTemplate(tpl)}
                                        className="text-left rounded-xl p-3 border-2 border-slate-200 hover:border-primary transition-[border-color] group"
                                        data-testid={`sf-industry-${tpl.code}`}
                                    >
                                        <div className={`h-20 rounded-lg bg-gradient-to-br ${tpl.preview} flex items-center justify-center text-3xl shadow-inner`}>
                                            <span>{tpl.emoji}</span>
                                        </div>
                                        <div className="mt-2 font-semibold text-sm">{tpl.name}</div>
                                        <div className="text-[10px] text-muted-foreground line-clamp-2">{tpl.tagline}</div>
                                        <div className="mt-1.5 text-[10px] text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Aplicar plantilla →</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="border-t pt-3" />
                        <ImageField label="Logo" value={cfg.logo_url} onChange={(v) => patch('logo_url', v)} hint="Recomendado: cuadrado 400×400 PNG transparente" testId="sf-logo-upload" />
                        <ImageField label="Banner (portada)" value={cfg.banner_url} onChange={(v) => patch('banner_url', v)} hint="Recomendado: 1920×480" testId="sf-banner-upload" />
                        <div>
                            <Label>Tema</Label>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                                {THEMES.map(t => (
                                    <button
                                        key={t.code}
                                        type="button"
                                        onClick={() => patch('theme', t.code)}
                                        className={`text-left rounded-xl p-3 border-2 transition-[border-color,box-shadow] ${cfg.theme === t.code ? 'border-primary shadow-md' : 'border-slate-200'}`}
                                        data-testid={`sf-theme-${t.code}`}
                                    >
                                        <div className={`h-16 rounded-lg ${t.preview}`} />
                                        <div className="mt-2 font-semibold text-sm">{t.name}</div>
                                        <div className="text-[10px] text-muted-foreground">{t.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Color primario</Label>
                                <div className="flex gap-2 items-center mt-1">
                                    <input type="color" value={cfg.primary_color || '#dc2626'} onChange={(e) => patch('primary_color', e.target.value)} className="h-9 w-12 rounded border cursor-pointer" data-testid="sf-primary-color" />
                                    <Input value={cfg.primary_color || ''} onChange={(e) => patch('primary_color', e.target.value)} placeholder="#dc2626" />
                                </div>
                            </div>
                            <div>
                                <Label>Color acento</Label>
                                <div className="flex gap-2 items-center mt-1">
                                    <input type="color" value={cfg.accent_color || '#0f172a'} onChange={(e) => patch('accent_color', e.target.value)} className="h-9 w-12 rounded border cursor-pointer" data-testid="sf-accent-color" />
                                    <Input value={cfg.accent_color || ''} onChange={(e) => patch('accent_color', e.target.value)} placeholder="#0f172a" />
                                </div>
                            </div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* HOMEPAGE */}
                <TabsContent value="homepage" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-4">
                        <div className="flex items-center justify-between p-3 border rounded">
                            <div>
                                <div className="text-sm font-semibold">Barra de anuncio</div>
                                <p className="text-xs text-muted-foreground">Mensaje corto que aparece arriba de tu tienda.</p>
                            </div>
                            <Switch checked={!!cfg.announcement_bar_enabled} onCheckedChange={(v) => patch('announcement_bar_enabled', v)} data-testid="sf-ann-enabled" />
                        </div>
                        {cfg.announcement_bar_enabled && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div><Label>Texto</Label><Input value={cfg.announcement_bar_text || ''} onChange={(e) => patch('announcement_bar_text', e.target.value)} placeholder="🎉 Envíos gratis en compras +$999" data-testid="sf-ann-text" /></div>
                                <div><Label>Link (opcional)</Label><Input value={cfg.announcement_bar_link || ''} onChange={(e) => patch('announcement_bar_link', e.target.value)} placeholder="https://…" /></div>
                            </div>
                        )}
                        <div className="border-t pt-3">
                            <div className="text-sm font-semibold mb-2">Hero de inicio</div>
                            <ImageField label="Imagen hero" value={cfg.hero_image_url} onChange={(v) => patch('hero_image_url', v)} hint="Recomendado: 1920×640" testId="sf-hero-upload" />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
                                <div className="md:col-span-3"><Label>Título</Label><Input value={cfg.hero_headline || ''} onChange={(e) => patch('hero_headline', e.target.value)} placeholder="Bienvenidos a mi tienda" data-testid="sf-hero-headline" /></div>
                                <div><Label>Texto botón</Label><Input value={cfg.hero_cta_label || ''} onChange={(e) => patch('hero_cta_label', e.target.value)} placeholder="Ver catálogo" /></div>
                                <div className="md:col-span-2"><Label>Link del botón</Label><Input value={cfg.hero_cta_link || ''} onChange={(e) => patch('hero_cta_link', e.target.value)} placeholder="/store/mi-nick" /></div>
                            </div>
                        </div>
                        <div className="border-t pt-3">
                            <div className="text-sm font-semibold mb-2">Productos destacados</div>
                            <p className="text-xs text-muted-foreground mb-2">Elige hasta 8 anuncios que aparecerán en tu homepage.</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-64 overflow-auto p-1">
                                {ads.filter(a => a.status === 'published').map(a => {
                                    const on = (cfg.featured_ad_ids || []).includes(a.id);
                                    return (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => {
                                                const cur = new Set(cfg.featured_ad_ids || []);
                                                if (on) cur.delete(a.id); else if (cur.size < 8) cur.add(a.id); else return;
                                                patch('featured_ad_ids', Array.from(cur));
                                            }}
                                            className={`text-left rounded border-2 overflow-hidden transition-[border-color] ${on ? 'border-primary' : 'border-slate-200'}`}
                                            data-testid={`sf-featured-${a.id}`}
                                        >
                                            <div className="aspect-square bg-slate-100 overflow-hidden">
                                                <img src={a.media?.[0]?.url} alt={a.title} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="p-1.5 text-[11px] font-medium truncate">{a.title}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* COLLECTIONS */}
                <TabsContent value="collections" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h3 className="font-semibold">Colecciones</h3>
                                <p className="text-xs text-muted-foreground">Agrupa productos por categorías propias.</p>
                            </div>
                            <Button size="sm" onClick={() => openColDialog(null)} data-testid="sf-add-collection"><Plus className="h-4 w-4 mr-1" />Nueva colección</Button>
                        </div>
                        {collections.length === 0 ? (
                            <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded">Sin colecciones aún.</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {collections.map(col => (
                                    <div key={col.id} className="flex items-center gap-2 p-3 border rounded hover:bg-slate-50" data-testid={`sf-collection-${col.slug}`}>
                                        <div className="w-14 h-14 rounded overflow-hidden bg-slate-100 shrink-0">
                                            {col.cover_url && <img src={col.cover_url} alt={col.name} className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">{col.name}</div>
                                            <div className="text-[11px] text-muted-foreground">/{col.slug} · {(col.ad_ids || []).length} productos</div>
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => openColDialog(col)}>Editar</Button>
                                        <Button size="sm" variant="ghost" onClick={() => deleteCollection(col.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent></Card>
                </TabsContent>

                {/* PAGES / POLICIES */}
                <TabsContent value="pages" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-3">
                        <h3 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" />Políticas de la tienda</h3>
                        <div><Label>Política de envíos</Label>
                            <Textarea rows={4} value={cfg.policies_shipping || ''} onChange={(e) => patch('policies_shipping', e.target.value)} placeholder="Tiempos, costos, transportistas…" data-testid="sf-policy-shipping" />
                        </div>
                        <div><Label>Política de devoluciones</Label>
                            <Textarea rows={4} value={cfg.policies_returns || ''} onChange={(e) => patch('policies_returns', e.target.value)} placeholder="Plazo, condiciones, reembolso…" data-testid="sf-policy-returns" />
                        </div>
                        <div><Label>Política de privacidad</Label>
                            <Textarea rows={4} value={cfg.policies_privacy || ''} onChange={(e) => patch('policies_privacy', e.target.value)} placeholder="Cómo manejas datos personales…" data-testid="sf-policy-privacy" />
                        </div>
                    </CardContent></Card>
                    <Card><CardContent className="p-5">
                        <h3 className="font-semibold flex items-center gap-2 mb-2"><MessageCircle className="h-4 w-4" />Preguntas frecuentes</h3>
                        <div className="space-y-2">
                            {(cfg.faq || []).map((f, i) => (
                                <div key={i} className="border rounded p-2">
                                    <div className="flex gap-2">
                                        <Input value={f.q} onChange={(e) => patch('faq', (cfg.faq || []).map((x, j) => j === i ? { ...x, q: e.target.value } : x))} placeholder="Pregunta" />
                                        <Button size="sm" variant="ghost" onClick={() => patch('faq', (cfg.faq || []).filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                    <Textarea rows={2} className="mt-1" value={f.a} onChange={(e) => patch('faq', (cfg.faq || []).map((x, j) => j === i ? { ...x, a: e.target.value } : x))} placeholder="Respuesta" />
                                </div>
                            ))}
                            <div className="border-2 border-dashed rounded p-2">
                                <Input placeholder="Nueva pregunta" value={faqTmp.q} onChange={(e) => setFaqTmp({ ...faqTmp, q: e.target.value })} />
                                <Textarea placeholder="Respuesta" rows={2} className="mt-1" value={faqTmp.a} onChange={(e) => setFaqTmp({ ...faqTmp, a: e.target.value })} />
                                <Button size="sm" className="mt-2" onClick={() => { if (!faqTmp.q) return; patch('faq', [...(cfg.faq || []), faqTmp]); setFaqTmp({ q: '', a: '' }); }} data-testid="sf-add-faq"><Plus className="h-4 w-4 mr-1" />Añadir</Button>
                            </div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* SEO */}
                <TabsContent value="seo" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-3">
                        <div><Label>Título SEO <span className="text-[10px] text-muted-foreground">({(cfg.seo_title || '').length}/60)</span></Label>
                            <Input value={cfg.seo_title || ''} onChange={(e) => patch('seo_title', e.target.value)} placeholder="Mi Súper Tienda | Ok te lo vendo" maxLength={120} data-testid="sf-seo-title" />
                        </div>
                        <div><Label>Meta descripción <span className="text-[10px] text-muted-foreground">({(cfg.seo_description || '').length}/160)</span></Label>
                            <Textarea rows={2} value={cfg.seo_description || ''} onChange={(e) => patch('seo_description', e.target.value)} placeholder="Describe tu tienda para Google…" maxLength={300} data-testid="sf-seo-description" />
                        </div>
                        <div><Label>Palabras clave (comas)</Label>
                            <Input value={(cfg.seo_keywords || []).join(', ')} onChange={(e) => patch('seo_keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="electrónica, celulares, méxico" />
                        </div>
                        <ImageField label="Imagen Open Graph (para redes)" value={cfg.seo_og_image} onChange={(v) => patch('seo_og_image', v)} hint="1200×630 recomendado" testId="sf-og-upload" />
                        {/* Preview */}
                        <div className="p-3 bg-slate-50 rounded border">
                            <div className="text-xs text-muted-foreground mb-1">Vista previa en Google</div>
                            <div className="text-blue-800 text-base font-medium">{cfg.seo_title || cfg.display_name || 'Título de tu tienda'}</div>
                            <div className="text-emerald-700 text-xs">okgo.mx/store/{user?.nickname}</div>
                            <div className="text-slate-700 text-xs mt-1">{cfg.seo_description || cfg.tagline || 'Descripción de tu tienda'}</div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* ANALYTICS */}
                <TabsContent value="analytics" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 space-y-3">
                        <div><Label>Google Analytics ID</Label>
                            <Input value={cfg.google_analytics_id || ''} onChange={(e) => patch('google_analytics_id', e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="sf-ga-id" />
                        </div>
                        <div><Label>Facebook Pixel ID</Label>
                            <Input value={cfg.facebook_pixel_id || ''} onChange={(e) => patch('facebook_pixel_id', e.target.value)} placeholder="1234567890" data-testid="sf-fb-pixel" />
                        </div>
                        <p className="text-xs text-muted-foreground">Los códigos se insertan automáticamente en tu tienda pública para que puedas hacer seguimiento de visitas y conversiones.</p>
                    </CardContent></Card>
                </TabsContent>

                {/* SOCIALS */}
                <TabsContent value="socials" className="space-y-3 pt-4">
                    <Card><CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {['instagram', 'facebook', 'tiktok', 'twitter', 'whatsapp', 'website', 'youtube'].map(k => (
                            <div key={k}>
                                <Label className="capitalize">{k}</Label>
                                <Input value={(cfg.socials || {})[k] || ''} onChange={(e) => patchNested('socials', k, e.target.value)} placeholder={k === 'whatsapp' ? '+52551234567' : `https://…`} data-testid={`sf-social-${k}`} />
                            </div>
                        ))}
                    </CardContent></Card>
                </TabsContent>

                {/* ADVANCED */}
                <TabsContent value="advanced" className="space-y-3 pt-4">
                    <DomainWizard />
                    <Card><CardContent className="p-5 space-y-3">
                        <div className="border-t pt-3 space-y-3">
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Acepta OkWallet</div><p className="text-xs text-muted-foreground">Compras pagadas con la billetera OkGo.</p></div>
                                <Switch checked={cfg.accepts_okwallet !== false} onCheckedChange={(v) => patch('accepts_okwallet', v)} data-testid="sf-accepts-wallet" />
                            </div>
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Acepta Stripe (tarjeta)</div><p className="text-xs text-muted-foreground">Requiere cuenta de Stripe conectada.</p></div>
                                <Switch checked={!!cfg.accepts_stripe} onCheckedChange={(v) => patch('accepts_stripe', v)} data-testid="sf-accepts-stripe" />
                            </div>
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Mostrar stock</div></div>
                                <Switch checked={cfg.show_stock !== false} onCheckedChange={(v) => patch('show_stock', v)} />
                            </div>
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Mostrar envío estimado</div></div>
                                <Switch checked={cfg.show_estimated_shipping !== false} onCheckedChange={(v) => patch('show_estimated_shipping', v)} />
                            </div>
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Mostrar reseñas</div></div>
                                <Switch checked={cfg.show_reviews !== false} onCheckedChange={(v) => patch('show_reviews', v)} />
                            </div>
                            <div className="flex items-center justify-between">
                                <div><div className="text-sm font-medium">Mostrar "Powered by OkGo"</div><p className="text-xs text-muted-foreground">Requiere plan Premium para desactivar.</p></div>
                                <Switch checked={cfg.show_powered_by !== false} onCheckedChange={(v) => patch('show_powered_by', v)} />
                            </div>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* MESSAGES */}
                <TabsContent value="messages" className="space-y-2 pt-4">
                    {contacts.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">Sin mensajes de contacto todavía.</div>
                    ) : contacts.map(c => (
                        <Card key={c.id}><CardContent className="p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{c.name}</span>
                                <span className="text-xs text-muted-foreground">{c.email}</span>
                                <span className="ml-auto text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString('es-MX')}</span>
                            </div>
                            {c.subject && <div className="text-xs font-medium">{c.subject}</div>}
                            <p className="text-sm mt-1 whitespace-pre-wrap">{c.message}</p>
                        </CardContent></Card>
                    ))}
                </TabsContent>
            </Tabs>

            {/* Collection dialog */}
            <Dialog open={colDialog} onOpenChange={setColDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editingCol ? 'Editar colección' : 'Nueva colección'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Nombre</Label><Input value={colForm.name} onChange={(e) => setColForm({ ...colForm, name: e.target.value })} data-testid="col-name" /></div>
                            <div><Label>Slug</Label><Input value={colForm.slug} onChange={(e) => setColForm({ ...colForm, slug: e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase() })} placeholder="ofertas" data-testid="col-slug" /></div>
                        </div>
                        <div><Label>Descripción</Label><Textarea rows={2} value={colForm.description} onChange={(e) => setColForm({ ...colForm, description: e.target.value })} /></div>
                        <ImageField label="Portada" value={colForm.cover_url} onChange={(v) => setColForm({ ...colForm, cover_url: v })} testId="col-cover-upload" />
                        <div>
                            <Label>Productos ({colForm.ad_ids?.length || 0})</Label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1 max-h-56 overflow-auto p-1">
                                {ads.filter(a => a.status === 'published').map(a => {
                                    const on = (colForm.ad_ids || []).includes(a.id);
                                    return (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => {
                                                const cur = new Set(colForm.ad_ids || []);
                                                if (on) cur.delete(a.id); else cur.add(a.id);
                                                setColForm({ ...colForm, ad_ids: Array.from(cur) });
                                            }}
                                            className={`text-left rounded border-2 overflow-hidden ${on ? 'border-primary' : 'border-slate-200'}`}
                                        >
                                            <div className="aspect-square bg-slate-100 overflow-hidden">
                                                <img src={a.media?.[0]?.url} alt={a.title} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="p-1 text-[10px] truncate">{a.title}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setColDialog(false)}>Cancelar</Button>
                        <Button onClick={saveCollection} data-testid="col-save">Guardar colección</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
